/**
 * G4 — Solve Celebration.
 * 1:1 with assets/extracted/tier-2/G4-celebration.html.
 *
 * Layout: blurred backdrop graph + dimming veil + centered card with
 *   - small green check (32px) at top
 *   - "Solved." heading + "Today's Fudeto" subtitle
 *   - rarity badge (48px symbol + label)
 *   - stats section (Time / Attempts / Streak, vertical row layout)
 *   - primary "Share your trail" + secondary "Done"
 * Card animates in (translateY 30px → 0, opacity 0 → 1) with spring easing.
 * Stats fade in row-by-row 400-900ms.
 */
import { generateDailyPuzzle } from '../../engine/generate';
import { fmtNum, fmtTime, t } from '../../i18n/runtime';
import { profile } from '../../state/profile';
import { el, html, on } from '../html';
import type { RouteContext } from '../router';

const RARITY_SYMBOL: Record<string, string> = {
  common: '●', uncommon: '◆', rare: '▲', epic: '★', legendary: '✦',
};
const RARITY_LABEL: Record<string, string> = {
  common: 'COMMON', uncommon: 'UNCOMMON', rare: 'RARE', epic: 'EPIC', legendary: 'LEGENDARY',
};

export default function (ctx: RouteContext): HTMLElement {
  const date = ctx.params['date'] ?? '';
  const timeMs = Number(ctx.query.get('t') ?? '0');
  const attempts = Number(ctx.query.get('a') ?? '1');
  const puzzle = generateDailyPuzzle(date);
  const p = profile().get();
  const streakWas = Math.max(0, p.streak - 1);

  const root = el(html`
    <section class="screen-solve" data-rarity="${puzzle.rarity}" aria-label="${t('solve.title')}">
      <div class="solve-backdrop-veil" aria-hidden="true"></div>
      <div class="solve-card-wrap">
        <div class="solve-card">
          <div class="solve-check" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="14" stroke="currentColor" stroke-width="1.6" fill="none"/>
              <path d="M10 16.5 L14 20.5 L22 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h2 class="solve-heading">${t('solve.title')}</h2>
          <p class="solve-subtitle">${t('solve.subtitle')}</p>
          <div class="solve-rarity rarity-${puzzle.rarity}">
            <span class="solve-rarity-symbol">${RARITY_SYMBOL[puzzle.rarity]}</span>
            <span class="solve-rarity-label">${RARITY_LABEL[puzzle.rarity]}</span>
          </div>
          <div class="solve-stats">
            <div class="solve-stat">
              <span class="label">${t('solve.time')}</span>
              <span class="value">${fmtTime(timeMs / 1000)}</span>
            </div>
            <div class="solve-stat">
              <span class="label">${t('solve.attempts')}</span>
              <span class="value">${fmtNum(attempts)}</span>
            </div>
            <div class="solve-stat">
              <span class="label">${t('solve.streak')}</span>
              <span class="value">
                <span class="streak-cell">
                  <span class="streak-old">${fmtNum(streakWas)}</span>
                  <span class="streak-sep">→</span>
                  <span class="streak-new"><span class="streak-roller"><span>${fmtNum(streakWas)}</span><span>${fmtNum(p.streak)}</span></span></span>
                  <span> 🔥</span>
                </span>
              </span>
            </div>
          </div>
          <div class="solve-actions">
            <button class="solve-btn solve-btn-primary" data-share>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="margin-right:8px;vertical-align:-2px"><path d="M4 12 V20 A2 2 0 0 0 6 22 H18 A2 2 0 0 0 20 20 V12 M16 6 L12 2 L8 6 M12 2 V15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
              ${t('solve.share')}
            </button>
            <button class="solve-btn solve-btn-secondary" data-next>${t('solve.next')}</button>
          </div>
        </div>
      </div>
      <p class="solve-countdown" data-countdown></p>
    </section>
  `);
  // Apply rarity color via CSS variable on the host so .rarity-* tint propagates.
  root.style.setProperty('--rarity', `var(--rarity-${puzzle.rarity})`);

  on(root, 'click', '[data-share]', () => ctx.navigate(`/share/${date}?t=${timeMs}&a=${attempts}`));
  on(root, 'click', '[data-next]', () => ctx.navigate('/'));

  const cd = root.querySelector('[data-countdown]') as HTMLElement;
  const tick = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setUTCHours(24, 0, 0, 0);
    const ms = tomorrow.getTime() - now.getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    cd.textContent = t('solve.countdown', { h, m });
  };
  tick();
  const handle = setInterval(tick, 60000);
  root.addEventListener('beforeunmount', () => clearInterval(handle));
  return root;
}
